import React, { useEffect, useState, useMemo } from 'react';
import { FaKey } from 'react-icons/fa';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import PopupDialog from '../../common/PopupDialog/PopupDialog';
import { UsersRestDataProvider, User } from '../../../data/UsersRestDataProvider';
import InfoPanel from '../../common/InfoPanel/InfoPanel';
import { useTranslation } from 'react-i18next';

function formatDateTime(dt?: string) {
  if (!dt) return '—';
  const d = new Date(dt);
  if (isNaN(d.getTime())) return dt;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

const UserManagementTab: React.FC = () => {
  const { t } = useTranslation(['common', 'screens', 'validation']);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordUser, setPasswordUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordValidation, setPasswordValidation] = useState<string | null>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const provider = React.useMemo(() => new UsersRestDataProvider(), []);

  const columns: DetailedListColumn<User>[] = useMemo(() => [
    { key: 'username', header: t('screens:userManagement.columns.username'), dataField: 'username', type: 'string' },
    { key: 'fullName', header: t('screens:userManagement.columns.fullName'), dataField: 'fullName', type: 'string' },
    { key: 'email', header: t('screens:userManagement.columns.email'), dataField: 'email', type: 'string' },
    { key: 'role', header: t('screens:userManagement.columns.role'), dataField: 'role', type: 'select', options: [
      { label: 'Admin', value: 'Admin' },
      { label: 'Manager', value: 'Manager' },
      { label: 'User', value: 'User' },
    ] },
    {
      key: 'isActive',
      header: t('screens:userManagement.columns.active'),
      dataField: 'isActive',
      type: 'checkbox',
      align: 'center',
      render: (user: User) => (user.isActive ? t('common:yes') : t('common:no')),
    },
    { key: 'createdAt', header: t('screens:userManagement.columns.created'), dataField: 'createdAt', type: 'string', render: (user: User) => formatDateTime(user.createdAt) },
    { key: 'lastLogin', header: t('screens:userManagement.columns.lastLogin'), dataField: 'lastLogin', type: 'string', render: (user: User) => formatDateTime(user.lastLogin) },
  ], [t]);

  useEffect(() => {
    setLoading(true);
    provider.getAllAdmin()
      .then(setUsers)
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }, [provider]);

  const handleChange = async (newUsers: User[]) => {
    setLoading(true);
    setError(null);
    try {
      // Find added, updated, and deleted users
      const added = newUsers.filter(nu => !users.some(u => u.id === nu.id));
      const removed = users.filter(u => !newUsers.some(nu => nu.id === u.id));
      const updated = newUsers.filter(nu => users.some(u => u.id === nu.id && JSON.stringify(u) !== JSON.stringify(nu)));

      // Omit createdAt/lastLogin on create
      for (const user of added) {
        const { createdAt, lastLogin, ...userToSend } = user;
        await provider.createAdmin(userToSend);
      }
      for (const user of updated) if (user.id) await provider.updateAdmin(user.id, user);
      for (const user of removed) if (user.id) await provider.deleteAdmin(user.id);

      const refreshed = await provider.getAllAdmin();
      setUsers(refreshed);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPasswordDialog = (user: User) => {
    setPasswordUser(user);
    setNewPassword('');
    setRepeatPassword('');
    setPasswordError(null);
    setPasswordValidation(null);
    setShowPasswordDialog(true);
  };
  const handleClosePasswordDialog = () => {
    setShowPasswordDialog(false);
    setPasswordUser(null);
    setNewPassword('');
    setRepeatPassword('');
    setPasswordError(null);
    setPasswordValidation(null);
    setPasswordLoading(false);
  };

  // Password policy: at least 10 chars, upper, lower, digit, special
  const validatePassword = (pw: string, repeat: string): string | null => {
    if (!pw) return t('validation:passwordEmpty');
    if (pw !== repeat) return t('validation:passwordsMismatch');
    if (pw.length < 10) return t('validation:passwordTooShort');
    if (!/[A-Z]/.test(pw)) return t('validation:passwordNeedsUppercase');
    if (!/[a-z]/.test(pw)) return t('validation:passwordNeedsLowercase');
    if (!/[0-9]/.test(pw)) return t('validation:passwordNeedsDigit');
    if (!/[\W_]/.test(pw)) return t('validation:passwordNeedsSpecial');
    return null;
  };

  const handleChangePassword = async () => {
    const validation = validatePassword(newPassword, repeatPassword);
    setPasswordValidation(validation);
    if (validation) return;
    if (!passwordUser || !passwordUser.id) return;
    setPasswordLoading(true);
    setPasswordError(null);
    try {
      await provider.changePassword(passwordUser.id, newPassword);
      handleClosePasswordDialog();
    } catch (e: any) {
      setPasswordError(e.message || t('validation:passwordChangeFailed'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const userFormFields = useMemo(() => {
    return columns.map(col => {
      const type = col.type || 'string';
      
      if(col.key === 'username') 
      {
        return {
          ...col,
          label: col.header,
          type,
          disabled: true, // Username is derived from email
        };
      }
      if (col.key === 'role') {
        return {
          ...col,
          label: col.header,
          type,
          disabled: (data: User) => data?.username === 'admin',
        };
      }
      if (col.key === 'createdAt' || col.key === 'lastLogin') {
        return {
          ...col,
          label: col.header,
          type,
          disabled: true,
        };
      }
      return { ...col, label: col.header, type };
    });
  }, [columns]);

  return (
    <div className="user-management-tab">
      <SubsectionPanel title={t('screens:userManagement.title')}>
        {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
        <InfoPanel header="About user management">
          This screen allows you to manage users and their roles. Use the actions to add, edit, or remove users. When your tenant is connected to Entra ID
          (Azure AD), users can only log in using their Entra ID credentials. The admin user cannot be deleted or modified. In this case these users must submit
          still be added in this screen, to ensure they have the correct roles assigned and can access the application.
        </InfoPanel>
        <DetailedListComponent
          dataSource={{ data: users, setData: setUsers }}
          columns={columns}
          rowKey={u => u.id || ''}
          onChange={handleChange}
          actions={(user) => (
            <button
              className="btn btn-outline-primary btn-sm me-2"
              title={t('common:changePassword')}
              onClick={() => handleOpenPasswordDialog(user)}
            >
              <FaKey />
            </button>
          )}
          formFields={userFormFields}
          actionsColumnFixed={true}
          actionsColumnWidth={200}
        />
        {loading && <div style={{ marginTop: 12 }}>{t('common:loading')}</div>}
        <PopupDialog
          open={showPasswordDialog}
          title={t('screens:userManagement.changePasswordFor', { username: passwordUser?.username })}
          onClose={handleClosePasswordDialog}
          actions={
            <>
              <button className="btn btn-secondary" onClick={handleClosePasswordDialog} disabled={passwordLoading}>{t('common:cancel')}</button>
              <button className="btn btn-primary" onClick={handleChangePassword} disabled={passwordLoading || !newPassword.trim() || !repeatPassword.trim()}>
                {passwordLoading ? t('screens:userManagement.savingPassword') : t('common:changePassword')}
              </button>
            </>
          }
        >
          <div className="form-group">
            {passwordValidation && <div className="text-danger" style={{ fontSize: '0.95em', marginBottom: 4 }}>{passwordValidation}</div>}
            <label>{t('screens:userManagement.newPassword')}</label>
            <input
              type="password"
              className="form-control"
              value={newPassword}
              onChange={e => {
                setNewPassword(e.target.value);
                setPasswordValidation(validatePassword(e.target.value, repeatPassword));
              }}
              autoFocus
              disabled={passwordLoading}
            />
            <label style={{ marginTop: 8 }}>{t('screens:userManagement.repeatPassword')}</label>
            <input
              type="password"
              className="form-control"
              value={repeatPassword}
              onChange={e => {
                setRepeatPassword(e.target.value);
                setPasswordValidation(validatePassword(newPassword, e.target.value));
              }}
              disabled={passwordLoading}
            />
            {passwordError && <div className="text-danger mt-2">{passwordError}</div>}
          </div>
        </PopupDialog>
      </SubsectionPanel>
    </div>
  );
};

export default UserManagementTab;
